/**
 * Hooks de Gamificación (Sprint 10.19).
 *
 * Modelo:
 *   - achievements: catálogo administrable (público)
 *   - user_achievements: lo que cada user desbloqueó (público)
 *   - user_streaks: rachas diarias (público)
 *   - profiles.total_points: puntos acumulados (denormalizado)
 *
 * Visibilidad: PÚBLICA. Cualquier user logueado ve los logros y puntos
 * de otro al visitar su perfil (modelo Stack Overflow / GitHub).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useUser } from '@/lib/auth';

export type AchievementCategory = 'course' | 'community' | 'session' | 'streak' | 'misc';
export type AchievementTriggerType =
  | 'first_lesson' | 'courses_completed' | 'first_certificate'
  | 'first_post' | 'comments_count'
  | 'first_session'
  | 'streak_days';

export interface Achievement {
  id: string;
  code: string;
  name: string;
  description: string;
  icon: string;            // lucide-react icon name
  category: AchievementCategory;
  points: number;
  trigger_type: AchievementTriggerType;
  trigger_threshold: number;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface UserAchievement {
  id: string;
  user_id: string;
  achievement_id: string;
  unlocked_at: string;
}

export interface GamificationStats {
  total_points: number;
  current_streak: number;
  longest_streak: number;
  last_active_date: string | null;
  courses_completed: number;
  certificates_earned: number;
  community_posts: number;
  community_comments: number;
  sessions_completed: number;
  achievements_count: number;
}

// =====================================================================
// Catálogo de achievements (público)
// =====================================================================

export function useAllAchievements(opts?: { onlyActive?: boolean }) {
  return useQuery({
    queryKey: ['achievements', 'all', opts?.onlyActive ?? true],
    queryFn: async (): Promise<Achievement[]> => {
      let q = supabase
        .from('achievements')
        .select('*')
        .order('sort_order', { ascending: true });
      if (opts?.onlyActive ?? true) {
        q = q.eq('is_active', true);
      }
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return (data ?? []) as Achievement[];
    },
    staleTime: 5 * 60_000, // catálogo cambia poco
  });
}

// =====================================================================
// Stats + achievements desbloqueados de un user específico
// =====================================================================

export function useUserGamification(userId: string | undefined) {
  return useQuery({
    queryKey: ['gamification', 'user', userId],
    queryFn: async () => {
      if (!userId) return null;
      const [statsRes, achievementsRes] = await Promise.all([
        supabase.rpc('get_user_gamification_stats', { _user_id: userId }),
        supabase
          .from('user_achievements')
          .select('id, achievement_id, unlocked_at')
          .eq('user_id', userId)
          .order('unlocked_at', { ascending: false }),
      ]);
      if (statsRes.error) throw new Error(statsRes.error.message);
      if (achievementsRes.error) throw new Error(achievementsRes.error.message);

      const stats = ((statsRes.data ?? [])[0] ?? null) as GamificationStats | null;
      const unlocked = (achievementsRes.data ?? []) as UserAchievement[];
      return { stats, unlocked };
    },
    enabled: !!userId,
  });
}

export function useMyGamification() {
  const user = useUser();
  return useUserGamification(user?.id);
}

// =====================================================================
// Record activity (actualiza streak)
// =====================================================================

export interface ActivityResult {
  current_streak: number;
  longest_streak: number;
  extended: boolean;
}

export function useRecordActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ActivityResult | null> => {
      // Fecha LOCAL del navegador (YYYY-MM-DD) → la racha usa la medianoche de
      // CADA usuario, no la del server en UTC.
      const now = new Date();
      const clientDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const { data, error } = await supabase.rpc('record_user_activity', { _client_date: clientDate });
      if (error) throw new Error(error.message);
      const row = (data ?? [])[0];
      return row ? (row as ActivityResult) : null;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gamification'] });
    },
  });
}

// =====================================================================
// Admin: CRUD del catálogo
// =====================================================================

export function useUpdateAchievement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name?: string;
      description?: string;
      icon?: string;
      points?: number;
      sort_order?: number;
      is_active?: boolean;
    }) => {
      const { id, ...patch } = input;
      const { error } = await supabase.from('achievements').update(patch).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['achievements'] });
    },
  });
}

// =====================================================================
// Sprint G4 — Tienda de cupones (puntos canjeables)
// =====================================================================
//
// Hooks que envuelven el modelo de G1 (puntos + cupones):
//   - useMyPoints       → balance real del user (RPC get_user_points_balance)
//   - useCouponTiers    → catálogo público de 4 tiers (Bronce/Plata/Oro/Platino)
//   - useMyCoupons      → cupones del user (activos + canjeados + expirados)
//   - useRedeemCoupon   → mutation que llama redeem_points_for_coupon
//
// Errores del RPC redeem_points_for_coupon vienen como Error con message:
//   'tier_invalid' | 'cooldown_active' | 'insufficient_points' |
//   'user_id_required' | 'tier_required'
// La UI muestra un mensaje legible según el code.

export type CouponTierCode = 'bronze' | 'silver' | 'gold' | 'platinum';

export interface CouponTier {
  tier: CouponTierCode;
  display_name: string;
  points_cost: number;
  value_usd_cents: number;
  cooldown_days: number;
  sort_order: number;
  is_active: boolean;
}

export interface GamificationCoupon {
  id: string;
  user_id: string;
  tier: CouponTierCode;
  points_cost: number;
  value_usd_cents: number;
  code: string;
  generated_at: string;
  expires_at: string;
  redeemed_at: string | null;
  redeemed_booking_id: string | null;
}

/** Helpers de estado de cupón (puro en el cliente). */
export function isCouponActive(c: GamificationCoupon): boolean {
  return c.redeemed_at === null && new Date(c.expires_at).getTime() > Date.now();
}
export function isCouponExpired(c: GamificationCoupon): boolean {
  return c.redeemed_at === null && new Date(c.expires_at).getTime() <= Date.now();
}
export function isCouponRedeemed(c: GamificationCoupon): boolean {
  return c.redeemed_at !== null;
}

/**
 * Balance de puntos del user actual.
 * Fuente única de verdad para "cuántos pts puede canjear" — esto es lo
 * que el RPC redeem_points_for_coupon usa para validar.
 */
export function useMyPoints() {
  const user = useUser();
  return useQuery({
    queryKey: ['gamification', 'points-balance', user?.id],
    queryFn: async (): Promise<number> => {
      if (!user?.id) return 0;
      const { data, error } = await supabase.rpc('get_user_points_balance', {
        _user_id: user.id,
      });
      if (error) throw new Error(error.message);
      return Number(data ?? 0);
    },
    enabled: !!user?.id,
    staleTime: 30_000, // 30s — balance cambia poco entre canjes
  });
}

/**
 * Catálogo de los 4 tiers (Bronce / Plata / Oro / Platino).
 * Lectura pública (RLS abierta). Solo activos por default.
 */
export function useCouponTiers(opts?: { onlyActive?: boolean }) {
  return useQuery({
    queryKey: ['coupon-tiers', opts?.onlyActive ?? true],
    queryFn: async (): Promise<CouponTier[]> => {
      let q = supabase
        .from('coupon_tiers')
        .select('tier, display_name, points_cost, value_usd_cents, cooldown_days, sort_order, is_active')
        .order('sort_order', { ascending: true });
      if (opts?.onlyActive ?? true) q = q.eq('is_active', true);
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return (data ?? []) as CouponTier[];
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * Cupones del user — incluye activos, canjeados (booking aplicado) y
 * expirados. La UI filtra/agrupa según necesite.
 */
export function useMyCoupons() {
  const user = useUser();
  return useQuery({
    queryKey: ['gamification', 'my-coupons', user?.id],
    queryFn: async (): Promise<GamificationCoupon[]> => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('gamification_coupons')
        .select('*')
        .eq('user_id', user.id)
        .order('generated_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as GamificationCoupon[];
    },
    enabled: !!user?.id,
    staleTime: 30_000,
  });
}

/**
 * Canje atómico: pts → cupón de tier X.
 * El RPC SECURITY DEFINER aplica cooldown + balance check + lock por
 * (user, tier). Tras success, invalidamos balance, cupones y stats.
 */
export function useRedeemCoupon() {
  const qc = useQueryClient();
  const user = useUser();
  return useMutation({
    mutationFn: async (tier: CouponTierCode): Promise<GamificationCoupon> => {
      if (!user?.id) throw new Error('not_authenticated');
      const { data, error } = await supabase.rpc('redeem_points_for_coupon', {
        _user_id: user.id,
        _tier: tier,
      });
      if (error) throw new Error(error.message);
      return data as GamificationCoupon;
    },
    onSuccess: () => {
      // Tras canjear: balance baja, cupón nuevo aparece, stats no cambian
      // (los pts vienen del ledger que sigue ahí) pero igual refrescamos
      // por si la UI muestra denormalized.
      qc.invalidateQueries({ queryKey: ['gamification'] });
      qc.invalidateQueries({ queryKey: ['coupon-tiers'] });
    },
  });
}

// =====================================================================
// Sprint G5 — Validación de cupón antes del checkout
// =====================================================================
//
// Hook que llama el RPC validate_coupon_for_user(_code) para preview de
// descuento antes de iniciar el pago. NO marca el cupón como redeemed —
// solo lee. La aplicación final ocurre en el webhook tras el pago.

export interface CouponValidationResult {
  valid: boolean;
  coupon_id: string | null;
  tier: CouponTierCode | null;
  value_usd_cents: number | null;
  expires_at: string | null;
  error_code: 'code_required' | 'code_not_found' | 'already_redeemed' | 'expired' | 'unauthorized' | null;
}

export function useValidateCoupon() {
  return useMutation({
    mutationFn: async (code: string): Promise<CouponValidationResult> => {
      const { data, error } = await supabase.rpc('validate_coupon_for_user', {
        _code: code,
      });
      if (error) throw new Error(error.message);
      // El RPC RETURNS TABLE — Supabase devuelve un array de una sola fila.
      const row = Array.isArray(data) ? data[0] : data;
      return (row ?? {
        valid: false,
        coupon_id: null,
        tier: null,
        value_usd_cents: null,
        expires_at: null,
        error_code: 'code_not_found',
      }) as CouponValidationResult;
    },
  });
}

export function readableCouponError(code: CouponValidationResult['error_code']): string {
  switch (code) {
    case 'code_not_found':   return 'No encontramos ese código. Revisa que lo escribiste igual.';
    case 'already_redeemed': return 'Ya usaste este cupón en otra reserva.';
    case 'expired':          return 'Este cupón ya venció.';
    case 'code_required':    return 'Pega el código del cupón.';
    case 'unauthorized':     return 'Tu sesión expiró. Vuelve a iniciar sesión.';
    default:                 return 'No pudimos validar el código.';
  }
}

/**
 * Traduce el code de error del RPC redeem a un mensaje humano-friendly.
 * Útil para mostrar en toasts / modal.
 */
export function readableRedeemError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  if (msg.includes('insufficient_points')) {
    return 'No te alcanzan los puntos para este cupón.';
  }
  if (msg.includes('cooldown_active')) {
    return 'Ya canjeaste un cupón de este nivel recientemente. Espera a que se cumpla el período de espera.';
  }
  if (msg.includes('tier_invalid')) {
    return 'Este tier no está disponible.';
  }
  if (msg.includes('not_authenticated') || msg.includes('user_id_required')) {
    return 'Tu sesión expiró. Vuelve a iniciar sesión.';
  }
  return 'No pudimos generar el cupón. Prueba de nuevo en un momento.';
}
